<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import CalibrationAutoLocateDialog from './CalibrationAutoLocateDialog.vue'
import { useLiveStore } from '@/stores/live'
import { copyText } from '@/utils/clipboard'
import { buildInfo } from '@/services/build-info'
import { performanceDiagnostics } from '@/services/performance-diagnostics'

const props = defineProps<{ startupOccupied: boolean }>()
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()
const live = useLiveStore()
const { t } = useI18n()
let focusBeforeDialog: HTMLElement | null = null

watch([() => props.startupOccupied, () => live.firstUseCalibrationPhase], ([occupied, phase]) => {
  if (!occupied && phase === 'waiting') void live.runFirstUseCalibration()
}, { immediate: true, flush: 'post' })

watch(() => live.firstUseCalibrationOpen, open => {
  if (!open) return
  focusBeforeDialog = document.activeElement instanceof HTMLElement ? document.activeElement : null
  emit('visibility-change', true)
}, { flush: 'sync' })

function closed() {
  // A new attempt can open while an old dialog's leave animation is finishing.
  if (live.firstUseCalibrationOpen) return
  const target = focusBeforeDialog?.isConnected ? focusBeforeDialog
    : document.querySelector<HTMLElement>('[data-capture-window-action]:not([disabled])')
      ?? document.querySelector<HTMLElement>('#main-content')
  if (!props.startupOccupied) target?.focus({ preventScroll: true })
  focusBeforeDialog = null
  emit('visibility-change', false)
}

async function downloadFrame() {
  const frame = live.firstUseCalibrationFrame
  if (!frame) return
  try {
    const canvas = document.createElement('canvas')
    canvas.width = frame.width; canvas.height = frame.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Canvas unavailable')
    context.putImageData(frame, 0, 0)
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('Image unavailable')
    const url = URL.createObjectURL(blob), anchor = document.createElement('a')
    anchor.href = url; anchor.download = `calibration-${frame.width}x${frame.height}.png`
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 1_000)
  } catch { ElMessage.error(t('calibration.autoDialog.exportFailed')) }
}

async function copyDiagnostics() {
  try {
    const diagnostic = performanceDiagnostics.snapshot()
    const copied = await copyText(JSON.stringify({ build: buildInfo, environment: diagnostic.environment,
      capture: diagnostic.capture, layout: diagnostic.layout, outcome: live.firstUseCalibrationOutcome,
      ...live.firstUseCalibrationProgress }, null, 2))
    if (!copied) throw new Error('Clipboard unavailable')
    ElMessage.success(t('calibration.autoDialog.copied'))
  } catch { ElMessage.error(t('calibration.autoDialog.exportFailed')) }
}

onBeforeUnmount(() => emit('visibility-change', false))
</script>

<template>
  <CalibrationAutoLocateDialog first-use :open="live.firstUseCalibrationOpen" :busy="live.firstUseCalibrationBusy"
    :progress="live.firstUseCalibrationProgress" :outcome="live.firstUseCalibrationOutcome"
    :retained="live.firstUseCalibrationRetained" :has-frame="Boolean(live.firstUseCalibrationFrame)"
    @close="live.closeFirstUseCalibration" @cancel="live.skipFirstUseCalibration" @closed="closed"
    @retry="live.runFirstUseCalibration()" @retry-mesos="live.runFirstUseCalibration(['mesos'])"
    @retry-save="live.runFirstUseCalibration(live.firstUseCalibrationProgress.metrics, 'retry-save')"
    @download="downloadFrame" @copy="copyDiagnostics" />
</template>
